import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components/macro';
import { isEmpty, get, find, filter, groupBy } from 'lodash';
import Platform from 'platform';
import keys from 'config/keys';
import { ChannelType } from 'store/pages/loaders/loader-types';
import scaleSize from 'utils/scale-size';
import useLeaveFor from 'hooks/useLeaveFor';
import useButtonClick from 'hooks/useButtonClick';
import { epgActions } from 'store/epg';
import { useLoadEpg } from 'hooks/data';
import { playerActions } from 'store/player';
import useTimeRange from './useTimeRange';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import ScrollTopHack from 'components/ScrollTopHack';
import BackButton from 'components/BackButton';
import { EPGContext } from './EPGContext';
import MainView from './MainView';
import EpgLoader from './EPGLoader';

interface IEPGWidget {
  channels: ChannelType[];
  playingChannelId: number;
}

const LEFT_CODE = Platform.getKeyCode(keys.LEFT);
const RIGHT_CODE = Platform.getKeyCode(keys.RIGHT);
const BACK_CODE = Platform.getKeyCode(keys.BACK);

const CATEGORIES_ID = 'CHANNEL_CATEGORIES';

const isEPGListActiveElement = () => {
  const PATTERN = 'EPG_CHANNEL';
  return (document.activeElement.id || '').includes(PATTERN);
};

const CAT_ID = {
  ALL: 'ALL',
  FAVORITE: 'FAVORITE',
};

const categories = [
  {
    id: CAT_ID.ALL,
    title: 'All channels',
  } /*,
  {
    id: CAT_ID.FAVORITE,
    title: 'Favorites',
  },*/,
];

const StyledBackButton = styled(BackButton)`
  top: ${scaleSize(58)}px;
`;

const StyledEPGWrapper = styled.div`
  position: absolute;
  height: ${scaleSize(1080)}px;
  width: ${scaleSize(1920)}px;
  background: ${({ theme }) => theme.epgBackground};
  z-index: 2;
`;

const EPGWidget: React.FC<IEPGWidget> = ({ channels, playingChannelId }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { sheetStartTimeMs, sheetEndTimeMs, currentTimeMs } = useTimeRange();
  const [currentCategoryId, setCurrentCategoryId] = useState(CAT_ID.ALL);
  const [focusedChannelId, setFocusedChannelId] = useState(playingChannelId);
  const onlyFavorites = currentCategoryId === CAT_ID.FAVORITE;
  const { data: epg, isLoading } = useLoadEpg({ channelIds: channels.map((c) => c.id) });

  const channelsMapped = useMemo(() => {
    const epgGrouped = groupBy(epg, 'live_id');

    const source = onlyFavorites ? filter(channels, 'favorite') : channels;

    return source.map((channel) => {
      return { ...channel, epg: epgGrouped[channel.id] };
    });
  }, [channels, epg, onlyFavorites]);

  const viewChangeTriggerKeyCodes = useMemo(() => [LEFT_CODE, RIGHT_CODE], []);

  const changeView = useCallback(() => {
    const noPrograms = isEmpty(find(channelsMapped, { id: focusedChannelId })?.epg);

    if (!isEPGListActiveElement() || noPrograms) return;

    history.push(`/epg/${focusedChannelId}`);
  }, [channelsMapped, focusedChannelId, history]);

  const hideEpgAction = useCallback(() => {
    dispatch(epgActions.hide());
    // hack for player focus
    SpatialNavigationWrapper.focus('@BUTTONS_SET');
  }, [dispatch]);

  const onBackListener = useCallback(() => {
    const parentNodeId = get(document.activeElement, ['parentNode', 'id']);

    if (parentNodeId === CATEGORIES_ID) {
      hideEpgAction();
    } else {
      SpatialNavigationWrapper.focus(`@${CATEGORIES_ID}`);
    }
  }, [hideEpgAction]);

  const onCategoryEnter = useCallback((id) => setCurrentCategoryId(id), []);

  const onChannelEnter = useCallback(
    (item) => {
      if (item.id === playingChannelId) return hideEpgAction();
      dispatch(playerActions.navigateToPlayer({ item, useReplace: true }));
    },
    [playingChannelId, dispatch, hideEpgAction],
  );

  const onChannelFocus = useCallback((item) => setFocusedChannelId(item.id), []);

  useLeaveFor(history, hideEpgAction);
  useButtonClick(viewChangeTriggerKeyCodes, changeView);
  useButtonClick(BACK_CODE, onBackListener);

  return (
    <StyledEPGWrapper>
      <EPGContext.Provider value={{ sheetStartTimeMs, sheetEndTimeMs, currentTimeMs, isLoading }}>
        <StyledBackButton sectionName="EPG-BACK-BUTTON" onEnter={hideEpgAction} />
        <MainView
          currentCategoryId={currentCategoryId}
          categories={categories}
          onCategoryEnter={onCategoryEnter}
          channels={channelsMapped}
          playingChannelId={playingChannelId}
          focusedChannelId={focusedChannelId}
          onChannelEnter={onChannelEnter}
          onChannelFocus={onChannelFocus}
        />
        {isLoading && <EpgLoader />}
      </EPGContext.Provider>
    </StyledEPGWrapper>
  );
};

export default ScrollTopHack(EPGWidget);
